import * as React from 'react';
import { deleteMeeting } from '@/api/meetings';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarBlank as CalendarBlankIcon,
  ChatCircle as ChatCircleIcon,
  Trash as TrashIcon,
  User as UserIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQueryClient } from '@tanstack/react-query';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import type { Meeting } from '@/types/meeting';
import { paths } from '@/paths';
import { toTitleCase } from '@/utils/to-title-case';
import { UserContext } from '@/contexts/auth/user-context';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { ACCOUNT_ROLES } from '@/constants/account-roles';

interface MeetingsTableProps {
  rows: Meeting[];
  loading: boolean;
  error: Error | null;
}

export function MeetingsTable({ rows, loading, error }: MeetingsTableProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userContext = React.useContext(UserContext);

  const canEditOrDelete = React.useMemo(() => {
    if (!userContext?.user) return false;

    const accountRoleNames = (userContext.user.account_roles ?? []).map((r) => r.name);
    const communityRoleNames = (userContext.user.community_roles ?? []).map((r) => r.name);

    const isAccountAdmin = accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_ADMIN) ||
      accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_OWNER) ||
      accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_MANAGER);

    const isBoardMember = communityRoleNames.includes(COMMUNITY_ROLES.BOARD_MEMBER);

    return isAccountAdmin || isBoardMember;
  }, [userContext?.user]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getMeetingAge = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    return Math.ceil(Math.abs(now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleDelete = async (meetingId: string | number) => {
    try {
      await deleteMeeting(meetingId);
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast.success('Meeting deleted successfully');
    } catch {
      toast.error('Failed to delete meeting');
    }
  };

  const columns = React.useMemo<MRT_ColumnDef<Meeting>[]>(
    () => [
      {
        accessorKey: 'display_id',
        header: 'ID',
        size: 100,
        maxSize: 120,
        enableColumnFilter: false,
        enableSorting: true,
        muiTableHeadCellProps: { sx: { minWidth: 100, width: 100, maxWidth: 120 } },
        Cell: ({ cell }) => {
          const idDisplay = cell.row.original.display_id ?? `#${String(cell.row.original.id).slice(0, 8)}`;
          return (
            <Typography fontFamily="monospace" fontWeight={600} variant="body2">
              {idDisplay}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'meeting_type',
        header: 'Type',
        size: 120,
        maxSize: 140,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'select',
        filterSelectOptions:
          rows
            .map((r) => r.meeting_type?.name)
            .filter(Boolean)
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((name) => ({ text: toTitleCase(name || ''), value: name })) || [],
        muiTableHeadCellProps: { sx: { minWidth: 120, width: 120, maxWidth: 140 } },
        Cell: ({ cell }) => (
          <Chip
            label={toTitleCase(cell.row.original.meeting_type?.name || '')}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'held_on',
        header: 'Meeting Date',
        size: 160,
        maxSize: 180,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'date-range',
        muiTableHeadCellProps: { sx: { minWidth: 160, width: 160, maxWidth: 180 } },
        Cell: ({ cell }) => {
          const heldOn = cell.row.original.held_on;
          return (
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                <CalendarBlankIcon size={14} style={{ color: 'var(--mui-palette-text-secondary)' }} />
                <Typography fontWeight={600} variant="body2">
                  {heldOn ? dayjs(heldOn).format('MMM D, YYYY') : 'Not Scheduled'}
                </Typography>
              </Stack>
              {heldOn && (
                <Typography color="text.secondary" variant="caption">
                  {dayjs(heldOn).format('h:mm A')}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'agenda',
        header: 'Agenda',
        size: 280,
        maxSize: 350,
        grow: true,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'text' as const,
        muiTableHeadCellProps: { sx: { minWidth: 200 } },
        Cell: ({ cell }) => {
          const agenda = cell.row.original.agenda || '';
          const hasAgenda = agenda.length > 0;
          return (
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                <ChatCircleIcon size={14} style={{ color: 'var(--mui-palette-text-secondary)', flexShrink: 0 }} />
                <Typography fontWeight={600} variant="body2">
                  {hasAgenda ? 'Has Agenda' : 'No Agenda'}
                </Typography>
              </Stack>
              {hasAgenda && (
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {agenda.length > 100 ? `${agenda.slice(0, 100)}...` : agenda}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'created_by',
        header: 'Created By',
        size: 160,
        maxSize: 180,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'text' as const,
        muiTableHeadCellProps: { sx: { minWidth: 140, width: 160, maxWidth: 180 } },
        Cell: ({ cell }) => {
          const createdBy = cell.row.original.created_by;
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.200',
                }}
              >
                <UserIcon size={16} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={600} variant="body2" sx={{ lineHeight: 1.2 }}>
                  {createdBy ? `${createdBy.first_name} ${createdBy.last_name}` : 'Unknown'}
                </Typography>
                <Typography color="text.secondary" variant="caption" sx={{ display: 'block', lineHeight: 1.1 }}>
                  {createdBy?.email || ''}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        maxSize: 140,
        enableColumnFilter: false,
        enableSorting: true,
        muiTableHeadCellProps: { sx: { minWidth: 120, width: 120, maxWidth: 140 } },
        Cell: ({ cell }) => {
          const createdAt = cell.row.original.created_at;
          const age = getMeetingAge(createdAt);
          const isRecent = age <= 7;
          return (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.25 }}>
                {formatDate(createdAt)}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarBlankIcon
                  size={14}
                  style={{ color: isRecent ? 'var(--mui-palette-success-main)' : 'var(--mui-palette-text-secondary)' }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: isRecent ? 'success.main' : 'text.secondary', fontWeight: isRecent ? 600 : 400 }}
                >
                  {age} day{age !== 1 ? 's' : ''} ago
                </Typography>
              </Stack>
            </Box>
          );
        },
      },
    ],
    [rows]
  );

  const table = useMaterialReactTable<Meeting>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: false,
      sorting: [{ id: 'created_at', desc: true }],
      density: 'compact',
    },
    state: { isLoading: loading },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search all meetings...',
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => {
      const isPast = row.original.held_on
        ? dayjs(row.original.held_on).isBefore(dayjs())
        : false;
      return canEditOrDelete && !isPast ? (
        <Box onClick={(e) => e.stopPropagation()}>
          <Button
            color="error"
            size="small"
            startIcon={<TrashIcon size={16} />}
            onClick={() => handleDelete(row.original.uuid ?? row.original.id)}
          >
            Delete
          </Button>
        </Box>
      ) : null;
    },
    renderTopToolbar: ({ table }) => (
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          px: 2,
          py: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <MRT_GlobalFilterTextField table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleDensePaddingButton table={table} />
        </Stack>
      </Box>
    ),
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', borderRadius: 0 } },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableProps: { sx: { tableLayout: 'fixed' } },
    muiTableHeadProps: {
      sx: {
        '& tr th': {
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          py: 1.5,
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        '& tr td': {
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: '0.875rem',
          py: 1.5,
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(paths.meetings.meeting(row.original.uuid ?? row.original.id)),
      sx: {
        cursor: 'pointer',
        '&:hover td': { bgcolor: 'action.hover' },
      },
    }),
    muiTableContainerProps: { sx: { minHeight: 400 } },
  });

  return <MaterialReactTable table={table} />;
}
